/**
 * CommandPalette — globale Schnellnavigation per Cmd+K / Ctrl+K
 *
 * Tastatur:
 *   Cmd/Ctrl+K oeffnen
 *   ArrowUp/ArrowDown navigieren
 *   Enter oeffnen
 *   Escape schliessen
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import {
  Bot,
  Building,
  Building2,
  Cloud,
  Code,
  Database,
  Factory,
  FileText,
  FolderTree,
  Globe,
  GraduationCap,
  ImageIcon,
  List,
  Loader2,
  Mail,
  MailSearch,
  MapPin,
  MessageCircle,
  Package,
  Radio,
  Receipt,
  Ruler,
  Scale,
  Search,
  Server,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Tags,
  Ticket,
  Users,
} from "lucide-react"
import { useAuthStore } from "@/store/authStore"
import { useModuleStore } from "@/store/moduleStore"
import { useSecureShareStore } from "@/store/secureShareStore"
import { allNavItems, type NavItem } from "./Sidebar"
import { cn } from "@/lib/utils"
import { api, type QuickSearchResult } from "@/services/api"

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function fuzzyMatch(needle: string, haystack: string): boolean {
  if (!needle) return true
  const n = needle.toLowerCase()
  const h = haystack.toLowerCase()
  if (h.includes(n)) return true
  // Akzeptiere auch nicht-zusammenhaengende Treffer (z.B. "ujg" -> "Urlaub genehmigen")
  let i = 0
  for (const c of h) {
    if (c === n[i]) i++
    if (i === n.length) return true
  }
  return false
}

type CommandItem = NavItem & {
  path?: string
  keywords?: string
  dynamic?: boolean
  quickResult?: QuickSearchResult
}

const settingsCommandItems: CommandItem[] = [
  { title: "Module verwalten", href: "/admin/settings?tab=modules", icon: Package, section: "Einstellungen", path: "Einstellungen / Module", keywords: "module aktivieren deaktivieren verwalten" },
  { title: "Mandant", href: "/admin/settings?tab=tenant", icon: Building2, section: "Einstellungen", path: "Einstellungen / Allgemein", keywords: "firma tenant mandant branding farbe" },
  { title: "System", href: "/admin/settings?tab=system", icon: Globe, section: "Einstellungen", path: "Einstellungen / Allgemein", keywords: "system timezone datum sprache lazytime" },
  { title: "Stammdaten", href: "/admin/settings?tab=lookup", icon: List, section: "Einstellungen", path: "Einstellungen / Allgemein", keywords: "lookup tabellen listen grunddaten" },
  { title: "E-Mail", href: "/admin/settings?tab=email", icon: Mail, section: "Einstellungen", path: "Einstellungen / Kommunikation", keywords: "smtp mail email" },
  { title: "E-Mail Monitor", href: "/admin/settings?tab=imap", icon: MailSearch, section: "Einstellungen", path: "Einstellungen / Kommunikation", keywords: "imap monitor mailbox postfach" },
  { title: "Chat", href: "/admin/settings?tab=chat", icon: MessageCircle, section: "Einstellungen", path: "Einstellungen / Kommunikation", keywords: "matrix mattermost talk chat" },
  { title: "Google Workspace", href: "/admin/settings?tab=google-workspace", icon: Cloud, section: "Einstellungen", path: "Einstellungen / Integrationen", keywords: "gmail google service account oauth" },
  { title: "Microsoft", href: "/admin/settings?tab=microsoft", icon: Building2, section: "Einstellungen", path: "Einstellungen / Integrationen", keywords: "ms graph outlook office azure" },
  { title: "kSuite", href: "/admin/settings?tab=ksuite", icon: Cloud, section: "Einstellungen", path: "Einstellungen / Integrationen", keywords: "infomaniak ksuite" },
  { title: "KI-Anbieter", href: "/admin/settings?tab=ai", icon: Bot, section: "Einstellungen", path: "Einstellungen / Dienste", keywords: "openai anthropic gemini mistral perplexity provider llm" },
  { title: "KI-Dienste", href: "/admin/settings?tab=ai-services", icon: Sparkles, section: "Einstellungen", path: "Einstellungen / Dienste", keywords: "ki services prompts zuweisung modelle" },
  { title: "Geodaten", href: "/admin/settings?tab=here", icon: MapPin, section: "Einstellungen", path: "Einstellungen / Dienste", keywords: "here maps adressen geocoding" },
  { title: "Speicher", href: "/admin/settings?tab=storage", icon: Database, section: "Einstellungen", path: "Einstellungen / Dienste", keywords: "opencloud s3 minio dateien" },
  { title: "Abrechnung", href: "/admin/settings?tab=billing", icon: Receipt, section: "Einstellungen", path: "Einstellungen / Dienste", keywords: "easybill rechnungen api key" },
  { title: "API-Dokumentation", href: "/admin/settings?tab=api-docs", icon: Code, section: "Einstellungen", path: "Einstellungen / Dienste", keywords: "api swagger dokumentation" },
]

const lookupCommandItems: CommandItem[] = [
  { title: "Organisationstypen", href: "/admin/settings?tab=lookup&lookup=organization-types", icon: Building, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "firma behörde verein organisation typen" },
  { title: "Adresstypen", href: "/admin/settings?tab=lookup&lookup=address-types", icon: MapPin, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "adresse hauptadresse filiale lager" },
  { title: "Tags", href: "/admin/settings?tab=lookup&lookup=tags", icon: Tags, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "label markierung kunde lieferant vip" },
  { title: "Titel", href: "/admin/settings?tab=lookup&lookup=titles", icon: GraduationCap, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "dr prof akademisch kontakt titel" },
  { title: "Größenklassen", href: "/admin/settings?tab=lookup&lookup=company-sizes", icon: Ruler, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "mitarbeiter umsatz groesse unternehmensgroesse" },
  { title: "Rechtsformen", href: "/admin/settings?tab=lookup&lookup=legal-forms", icon: Scale, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "gmbh ag ev rechtsform" },
  { title: "Branchen", href: "/admin/settings?tab=lookup&lookup=industries", icon: Factory, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "wirtschaftszweig industrie branche" },
  { title: "Standardabteilungen", href: "/admin/settings?tab=lookup&lookup=department-templates", icon: Building2, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "abteilungen kunden abteilung vorlagen" },
  { title: "Standardfunktionen", href: "/admin/settings?tab=lookup&lookup=function-templates", icon: FileText, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "funktion rolle vorlagen position" },
  { title: "Medien", href: "/admin/settings?tab=lookup&lookup=communication-media", icon: Radio, section: "Stammdaten", path: "Einstellungen / Stammdaten / Kommunikation", keywords: "telefon email messaging kommunikationsmedien" },
  { title: "Kanäle", href: "/admin/settings?tab=lookup&lookup=communication-channels", icon: MessageCircle, section: "Stammdaten", path: "Einstellungen / Stammdaten / Kommunikation", keywords: "whatsapp teams linkedin kanal dienst" },
  { title: "Kontexte", href: "/admin/settings?tab=lookup&lookup=communication-contexts", icon: Users, section: "Stammdaten", path: "Einstellungen / Stammdaten / Kommunikation", keywords: "privat geschäftlich kunde kontext" },
  { title: "Geräte", href: "/admin/settings?tab=lookup&lookup=communication-devices", icon: Smartphone, section: "Stammdaten", path: "Einstellungen / Stammdaten / Kommunikation", keywords: "handy telefon firmengeraet geraet" },
  { title: "Logos", href: "/admin/settings?tab=lookup&lookup=logos", icon: ImageIcon, section: "Stammdaten", path: "Einstellungen / Stammdaten", keywords: "logo favicon branding bild marke" },
]

const extraCommandItems: CommandItem[] = [
  { title: "Server Health TV", href: "/tv/server-health", icon: Server, section: "Administration", path: "Monitoring", keywords: "server health status tv dashboard monitor last systemzustand", requiredRoles: ["admin"] },
  ...settingsCommandItems.map((item) => ({ ...item, requiredRoles: ["admin"] })),
  ...lookupCommandItems.map((item) => ({ ...item, requiredRoles: ["admin"] })),
]

const quickResultTypes: Record<string, Pick<CommandItem, "icon" | "section" | "requiredModule" | "requiredPermission">> = {
  organization: { icon: Building2, section: "Organisationen", requiredPermission: "organizations:read" },
  contact: { icon: Users, section: "Kontakte", requiredPermission: "contacts:read" },
  ticket: { icon: MessageCircle, section: "Tickets", requiredModule: "tickets" },
  project: { icon: FolderTree, section: "Projekte", requiredModule: "hr", requiredPermission: "hr_project:read" },
  invoice_incoming: { icon: FileText, section: "Rechnungen Eingang", requiredModule: "rechnungen", requiredPermission: "rechnungen:read" },
  invoice_outgoing: { icon: Receipt, section: "Rechnungen Ausgang", requiredModule: "rechnungen", requiredPermission: "rechnungen:read" },
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
}

function acronym(value: string): string {
  return normalize(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
}

function scoreItem(query: string, item: CommandItem): number | null {
  const q = normalize(query.trim())
  if (!q) return 0
  const title = normalize(item.title)
  const path = normalize(item.path || item.section || "")
  const keywords = normalize(item.keywords || "")
  const href = normalize(item.href)
  const haystack = `${title} ${path} ${keywords} ${href}`

  if (title === q) return 1000
  if (title.startsWith(q)) return 900
  if (haystack.includes(q)) return 700
  if (haystack.split(/[^a-z0-9]+/).some((word) => word.startsWith(q))) return 600
  if (acronym(`${item.path || ""} ${item.title}`).startsWith(q)) return 500
  if (q.length <= 3 && fuzzyMatch(q, haystack)) return 200
  return null
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { hasRole, hasPermission, isAdmin } = useAuthStore()
  const { isModuleEnabled } = useModuleStore()
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const trimmedQuery = query.trim()
  const trimmedDebouncedQuery = debouncedQuery.trim()

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim()), 180)
    return () => window.clearTimeout(handle)
  }, [query])

  const { data: quickResults = [], isFetching: quickSearchLoading } = useQuery<QuickSearchResult[]>({
    queryKey: ["command-palette", "quick-search", trimmedDebouncedQuery],
    queryFn: () => api.quickSearch(trimmedDebouncedQuery, 16),
    enabled: open && trimmedDebouncedQuery.length >= 2,
    staleTime: 1000 * 30,
  })

  // Items filtern: erst nach Berechtigungen + Modul, dann nach Suchbegriff
  const navigationItems = useMemo(() => {
    return [...allNavItems, ...extraCommandItems].flatMap((item) => {
      if (item.requiredModule && !isModuleEnabled(item.requiredModule)) return []
      if (item.requiredPermission && !hasPermission(item.requiredPermission)) return []
      if (item.requiredRoles && !isAdmin()) {
        if (!item.requiredRoles.some((r) => hasRole(r))) return []
      }
      if ((item as CommandItem).dynamic && trimmedQuery.length === 0) return []
      const score = scoreItem(query, item)
      return score === null ? [] : [{ item, score }]
    }).sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title, "de")).map(({ item }) => item)
  }, [query, isModuleEnabled, hasRole, hasPermission, isAdmin])

  const searchResultItems = useMemo<CommandItem[]>(() => {
    if (trimmedQuery.length < 2 || trimmedDebouncedQuery !== trimmedQuery) return []
    return quickResults.flatMap((result) => {
      const typeConfig = quickResultTypes[result.type] || { icon: Search, section: "Treffer" }
      if (typeConfig.requiredModule && !isModuleEnabled(typeConfig.requiredModule)) return []
      if (typeConfig.requiredPermission && !hasPermission(typeConfig.requiredPermission)) return []
      return [{
        title: result.title,
        href: result.href,
        icon: typeConfig.icon,
        section: typeConfig.section,
        path: result.subtitle || typeConfig.section,
        quickResult: result,
      }]
    })
  }, [quickResults, trimmedDebouncedQuery, trimmedQuery, isModuleEnabled, hasPermission])

  const itemGroups = useMemo(() => {
    if (trimmedQuery.length >= 2) {
      return [
        { label: "Treffer", items: searchResultItems },
        { label: "Navigation", items: navigationItems },
      ].filter((group) => group.items.length > 0)
    }
    return [{ label: "Navigation", items: navigationItems }]
  }, [navigationItems, searchResultItems, trimmedQuery.length])

  const items = useMemo(
    () => itemGroups.flatMap((group) => group.items),
    [itemGroups],
  )

  // Reset bei Oeffnen
  useEffect(() => {
    if (open) {
      setQuery("")
      setDebouncedQuery("")
      setActiveIdx(0)
      setTimeout(() => inputRef.current?.focus(), 10)
    }
  }, [open])

  // ActiveIdx clampen wenn sich Items aendern
  useEffect(() => {
    if (activeIdx >= items.length) setActiveIdx(Math.max(0, items.length - 1))
  }, [items.length, activeIdx])

  // Active-Item ins Sichtfeld scrollen
  useEffect(() => {
    if (!listRef.current) return
    const active = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
    active?.scrollIntoView({ block: "nearest" })
  }, [activeIdx])

  function selectItem(item: NavItem) {
    onOpenChange(false)
    navigate(item.href)
  }

  // Kontakt-Treffer: Secure-Share-Dialog (Secret senden oder Einladung) mit
  // vorbefüllter Empfängeradresse öffnen
  async function openSecureShare(result: QuickSearchResult, mode: "send" | "invite") {
    onOpenChange(false)
    const contactId = (result.href.split("/contacts/")[1] || "").split(/[?#]/)[0]
    let email = ""
    if (contactId) {
      try {
        const comms = await api.getCommunications({ entityType: "contact", entityId: contactId })
        const emails = comms.filter(
          (c) => (c.mediumName?.toLowerCase().includes("mail") || c.value?.includes("@")) && c.value,
        )
        const chosen = emails.find((c) => c.isPrimary) || emails[0]
        email = chosen?.value || ""
      } catch {
        /* ohne E-Mail öffnen — manuelle Eingabe bleibt möglich */
      }
    }
    useSecureShareStore.getState().openWith({ email, name: result.title, mode })
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIdx((i) => Math.min(items.length - 1, i + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const item = items[activeIdx]
      if (item) selectItem(item)
    } else if (e.key === "Escape") {
      onOpenChange(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/40 backdrop-blur-sm"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full max-w-xl mx-4 rounded-lg bg-popover text-popover-foreground shadow-2xl border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Suche */}
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0) }}
            onKeyDown={handleKey}
            placeholder="Suchen oder navigieren..."
            className="flex-1 bg-transparent outline-none text-sm py-1.5"
          />
          {quickSearchLoading && trimmedQuery.length >= 2 && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
          <kbd className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        {/* Ergebnisliste */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {quickSearchLoading && trimmedQuery.length >= 2 ? "Suche laeuft..." : "Nichts gefunden."}
            </div>
          )}
          {(() => {
            let idx = 0
            return itemGroups.map((group) => (
              <div key={group.label}>
                {itemGroups.length > 1 && (
                  <div className="px-3 pb-1 pt-2 text-[10px] font-medium uppercase text-muted-foreground">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => {
                  const currentIdx = idx++
                  const Icon = item.icon
                  const isActive = currentIdx === activeIdx
                  const quickResult = (item as CommandItem).quickResult
                  const isContact = quickResult?.type === "contact"
                  return (
                    <div key={`${item.href}-${currentIdx}`} className="relative">
                      <button
                        data-idx={currentIdx}
                        onClick={() => selectItem(item)}
                        onMouseEnter={() => setActiveIdx(currentIdx)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 text-sm text-left",
                          isContact && "pr-52",
                          isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/50",
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{item.title}</span>
                          {(item as CommandItem).path && (
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {(item as CommandItem).path}
                            </span>
                          )}
                        </span>
                        {item.section && !(item as CommandItem).path && (
                          <span className="shrink-0 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {item.section}
                          </span>
                        )}
                      </button>
                      {isContact && quickResult && (
                        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openSecureShare(quickResult, "send") }}
                            className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                            title="Secret sicher per E-Mail senden"
                          >
                            <ShieldCheck className="h-3.5 w-3.5" /> SecureMail
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openSecureShare(quickResult, "invite") }}
                            className="inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-accent"
                            title="Einladungslink für diesen Kontakt erstellen"
                          >
                            <Ticket className="h-3.5 w-3.5" /> Einladung
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ))
          })()}
        </div>

        {/* Footer */}
        <div className="border-t px-3 py-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{items.length} Eintrag{items.length === 1 ? "" : "e"}</span>
          <span className="flex items-center gap-2">
            <span>↑↓ navigieren</span>
            <span>↵ oeffnen</span>
          </span>
        </div>
      </div>
    </div>
  )
}
